import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/frontend/contexts/AuthContext'

/**
 * LINE Login callback page
 * Handles the OAuth callback from LINE and completes Firebase authentication
 */
export default function LineCallbackPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { handleLineCallback, getLineAuthError, isSignedIn } = useAuth()

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        const error = urlParams.get('error')
        const errorDescription = urlParams.get('error_description')

        if (error) {
          throw new Error(errorDescription || error)
        }

        if (!token) {
          throw new Error('No Firebase token received from LINE authentication')
        }

        console.log('[LineCallback] Processing LINE authentication callback with Firebase token')
        
        // Handle the callback through AuthContext
        await handleLineCallback(window.location.href)
        
        console.log('[LineCallback] Authentication successful, redirecting...')
        
        // Short delay to ensure auth state is updated
        setTimeout(() => {
          navigate('/', { replace: true })
        }, 1000)

      } catch (error: any) {
        console.error('[LineCallback] Authentication failed:', error)
        setError(getLineAuthError(error))
        setLoading(false)
      }
    }

    // Only process if not already signed in
    if (!isSignedIn) {
      processCallback()
    } else {
      // Already signed in, redirect to home
      navigate('/', { replace: true })
    }
  }, [handleLineCallback, getLineAuthError, isSignedIn, navigate])

  const handleRetry = () => {
    navigate('/sign-in', { replace: true })
  }

  const handleBackToHome = () => {
    navigate('/', { replace: true })
  }

  // Show loading state
  if (loading && !error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-5">
        <Card className="w-full max-w-md shadow-2xl border-0 rounded-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-blue-600">
              Completing Sign In
            </CardTitle>
            <CardDescription className="text-gray-600">
              Processing your LINE authentication...
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <div className="text-center text-sm text-gray-600">
              Please wait while we complete your sign in.
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-5">
        <Card className="w-full max-w-md shadow-2xl border-0 rounded-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-red-600">
              Sign In Failed
            </CardTitle>
            <CardDescription className="text-gray-600">
              There was a problem with your LINE authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            
            <div className="flex flex-col space-y-3">
              <Button onClick={handleRetry} className="w-full">
                Try Again
              </Button>
              <Button 
                variant="outline" 
                onClick={handleBackToHome} 
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // This should not happen, but just in case
  return null
}